import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  checkProviderConnectionHealth,
  getAiTestRuns,
  getAnalyticsSnapshots,
  getApiErrorMessage,
  getBotLogs,
  getModels,
  getProactiveRuns,
  smokeSendTelegramAccount,
  syncFanslyMessages,
  syncFanvueMessages,
} from '../../api'
import { EmptyState, LoadingBlock, MetricCard, Section, StatusPill } from '../../components/v2/V2Primitives'
import {
  asList,
  compactNumber,
  formatDate,
  modelTitle,
  providerLabel,
  providerLinks,
  providerStatus,
  statusTone,
} from './v2Utils'

function providerRows(models) {
  return asList(models).flatMap((model) => providerLinks(model).map((link) => ({ model, link })))
}

function isAttentionStatus(status) {
  return ['failed', 'error', 'blocked', 'disconnected'].includes(String(status || '').toLowerCase())
}

function parseIdCsv(value) {
  return String(value || '')
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0)
}

export default function AdminV2Diagnostics() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [models, setModels] = useState([])
  const [botLogs, setBotLogs] = useState([])
  const [proactiveRuns, setProactiveRuns] = useState([])
  const [analyticsSnapshots, setAnalyticsSnapshots] = useState([])
  const [aiTestRuns, setAiTestRuns] = useState([])
  const [busyKey, setBusyKey] = useState('')
  const [actionStatus, setActionStatus] = useState('')
  const [telegramSmokeDrafts, setTelegramSmokeDrafts] = useState({})
  const [smokeResults, setSmokeResults] = useState({})
  const [runtimeLoading, setRuntimeLoading] = useState(false)
  const [runtimeError, setRuntimeError] = useState('')

  async function loadModels() {
    setLoading(true)
    setError('')
    try {
      const result = await getModels()
      setModels(asList(result))
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load diagnostics'))
    } finally {
      setLoading(false)
    }
  }

  async function loadRuntimeDiagnostics() {
    setRuntimeLoading(true)
    setRuntimeError('')
    const results = await Promise.allSettled([
      getBotLogs({ limit: 5 }),
      getProactiveRuns({ limit: 5 }),
      getAnalyticsSnapshots({ limit: 5 }),
      getAiTestRuns({ limit: 5 }),
    ])
    const setters = [setBotLogs, setProactiveRuns, setAnalyticsSnapshots, setAiTestRuns]
    const labels = ['bot logs', 'proactive runs', 'analytics snapshots', 'AI test runs']
    const errors = []
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        setters[index](asList(result.value))
      } else {
        setters[index]([])
        errors.push(`${labels[index]}: ${getApiErrorMessage(result.reason, 'load failed')}`)
      }
    })
    if (errors.length) setRuntimeError(`Some diagnostics failed - ${errors.join('; ')}`)
    setRuntimeLoading(false)
  }

  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      try {
        const result = await getModels()
        if (alive) setModels(asList(result))
      } catch (err) {
        if (alive) setError(getApiErrorMessage(err, 'Failed to load diagnostics'))
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    loadRuntimeDiagnostics()
    return () => { alive = false }
  }, [])

  const rows = useMemo(() => providerRows(models), [models])
  const realRows = rows.filter(({ link }) => link.id)
  const connected = rows.filter(({ link }) => providerStatus(link) === 'connected')
  const attention = rows.filter(({ link }) => isAttentionStatus(providerStatus(link)))
  const stale = realRows.filter(({ link }) => !link.connection_updated_at)
  const fanvueSyncRows = realRows.filter(({ link }) => String(link.provider || '').toLowerCase() === 'fanvue' && providerStatus(link) === 'connected')
  const fanslySyncRows = realRows.filter(({ link }) => String(link.provider || '').toLowerCase() === 'fansly' && providerStatus(link) === 'connected')
  const telegramSmokeRows = realRows.filter(({ link }) => String(link.provider || '').toLowerCase() === 'telegram_account' && providerStatus(link) === 'connected')
  const failedAiRuns = aiTestRuns.filter((item) => isAttentionStatus(item.status))
  const runningAiRuns = aiTestRuns.filter((item) => ['running', 'queued'].includes(String(item.status || '').toLowerCase()))

  function mergeUpdatedModel(updated) {
    if (!updated?.id) return
    setModels((current) => current.map((model) => (model.id === updated.id ? { ...model, ...updated } : model)))
  }

  async function runHealthCheck(modelId, linkId) {
    if (!modelId || !linkId) return null
    const key = `${modelId}:${linkId}`
    setBusyKey(key)
    setActionStatus('')
    setError('')
    try {
      const updated = await checkProviderConnectionHealth(modelId, linkId)
      mergeUpdatedModel(updated)
      const checkedLink = providerLinks(updated).find((item) => String(item.id) === String(linkId))
      const status = providerStatus(checkedLink)
      setActionStatus(`${providerLabel(checkedLink?.provider)} health check finished: ${status}.`)
      return updated
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to check provider health'))
      return null
    } finally {
      setBusyKey('')
    }
  }

  async function runAllHealthChecks() {
    const checkable = realRows
    if (!checkable.length) return
    setActionStatus('')
    setError('')
    let ok = 0
    for (const { model, link } of checkable) {
      const updated = await runHealthCheck(model.id, link.id)
      if (updated) ok += 1
    }
    setActionStatus(`Provider health checks finished for ${ok}/${checkable.length} accounts.`)
  }

  async function runFanvueSyncSmoke(model, link) {
    if (!model?.id || !link?.id) return null
    const key = `fanvue-sync:${model.id}:${link.id}`
    setBusyKey(key)
    setActionStatus('')
    setError('')
    setSmokeResults((current) => ({
      ...current,
      [key]: { tone: 'neutral', label: 'Running', detail: 'Fanvue sync smoke is running.' },
    }))
    try {
      const result = await syncFanvueMessages(model.id, {
        limit: 5,
        maxChats: 5,
        enqueueIncoming: false,
      })
      const seen = result?.messages_seen ?? 0
      const created = result?.messages_created ?? 0
      const updated = result?.messages_updated ?? 0
      const errors = Array.isArray(result?.errors) ? result.errors.length : 0
      const detail = `${seen} seen, ${created} created, ${updated} updated, ${errors} errors`
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: errors ? 'warn' : 'good', label: result?.ok ? 'Passed' : 'Finished', detail },
      }))
      setActionStatus(`Fanvue sync smoke finished for ${modelTitle(model)}: ${seen} seen, ${created} created, ${updated} updated, ${errors} errors.`)
      return result
    } catch (err) {
      const detail = getApiErrorMessage(err, 'Failed to run Fanvue sync smoke')
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: 'bad', label: 'Failed', detail },
      }))
      setError(detail)
      return null
    } finally {
      setBusyKey('')
    }
  }

  async function runAllFanvueSyncSmokes() {
    if (!fanvueSyncRows.length) return
    setActionStatus('')
    setError('')
    let ok = 0
    for (const { model, link } of fanvueSyncRows) {
      const result = await runFanvueSyncSmoke(model, link)
      if (result?.ok) ok += 1
    }
    setActionStatus(`Fanvue sync smoke finished for ${ok}/${fanvueSyncRows.length} accounts.`)
  }

  async function runFanslySyncSmoke(model, link) {
    if (!model?.id || !link?.id) return null
    const key = `fansly-sync:${model.id}:${link.id}`
    setBusyKey(key)
    setActionStatus('')
    setError('')
    setSmokeResults((current) => ({
      ...current,
      [key]: { tone: 'neutral', label: 'Running', detail: 'Fansly sync smoke is running.' },
    }))
    try {
      const result = await syncFanslyMessages(model.id, {
        limit: 5,
        maxChats: 5,
        enqueueUnanswered: false,
      })
      const seen = result?.messages_seen ?? 0
      const created = result?.messages_created ?? 0
      const updated = result?.messages_updated ?? 0
      const errors = Array.isArray(result?.errors) ? result.errors.length : 0
      const detail = `${seen} seen, ${created} created, ${updated} updated, ${errors} errors`
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: errors ? 'warn' : 'good', label: result?.ok ? 'Passed' : 'Finished', detail },
      }))
      setActionStatus(`Fansly sync smoke finished for ${modelTitle(model)}: ${seen} seen, ${created} created, ${updated} updated, ${errors} errors.`)
      return result
    } catch (err) {
      const detail = getApiErrorMessage(err, 'Failed to run Fansly sync smoke')
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: 'bad', label: 'Failed', detail },
      }))
      setError(detail)
      return null
    } finally {
      setBusyKey('')
    }
  }

  async function runAllFanslySyncSmokes() {
    if (!fanslySyncRows.length) return
    setActionStatus('')
    setError('')
    let ok = 0
    for (const { model, link } of fanslySyncRows) {
      const result = await runFanslySyncSmoke(model, link)
      if (result?.ok) ok += 1
    }
    setActionStatus(`Fansly sync smoke finished for ${ok}/${fanslySyncRows.length} accounts.`)
  }

  function updateTelegramSmokeDraft(modelId, patch) {
    setTelegramSmokeDrafts((current) => ({
      ...current,
      [modelId]: {
        fanId: '',
        text: 'Telegram account smoke test',
        ...(current[modelId] || {}),
        ...patch,
      },
    }))
  }

  async function runTelegramSmoke(model, link) {
    const draft = telegramSmokeDrafts[model.id] || {}
    const fanId = String(draft.fanId || '').trim()
    const text = String(draft.text || 'Telegram account smoke test').trim()
    const mediaIds = parseIdCsv(draft.mediaIds)
    if (!fanId || !text) {
      setError('Telegram smoke requires fan id and message text.')
      return null
    }
    const mediaLabel = mediaIds.length ? ` with ${mediaIds.length} media file(s)` : ''
    if (!window.confirm(`Send Telegram smoke message${mediaLabel} for ${modelTitle(model)} to fan #${fanId}?`)) return null
    const key = `telegram-smoke:${model.id}:${link.id}`
    setBusyKey(key)
    setActionStatus('')
    setError('')
    setSmokeResults((current) => ({
      ...current,
      [key]: { tone: 'neutral', label: 'Running', detail: 'Telegram smoke send is running.' },
    }))
    try {
      const result = await smokeSendTelegramAccount(model.id, { fanId, text, mediaIds })
      const sentMedia = Array.isArray(result?.media_ids) ? result.media_ids.length : 0
      const detail = `${result?.peer || `fan #${fanId}`}${sentMedia ? `, ${sentMedia} media file(s)` : ''}`
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: 'good', label: 'Sent', detail },
      }))
      setActionStatus(`Telegram smoke sent for ${modelTitle(model)} to ${result?.peer || `fan #${fanId}`}${sentMedia ? ` with ${sentMedia} media file(s)` : ''}.`)
      return result
    } catch (err) {
      const detail = getApiErrorMessage(err, 'Failed to send Telegram smoke message')
      setSmokeResults((current) => ({
        ...current,
        [key]: { tone: 'bad', label: 'Failed', detail },
      }))
      setError(detail)
      return null
    } finally {
      setBusyKey('')
    }
  }

  if (loading) return <LoadingBlock label="Loading diagnostics..." />

  return (
    <div className="v2-stack">
      <div className="v2-metrics-grid">
        <MetricCard label="Provider slots" value={compactNumber(rows.length)} detail="Configured and pending slots" />
        <MetricCard label="Connected" value={compactNumber(connected.length)} detail="Ready provider links" tone="good" />
        <MetricCard label="Need attention" value={compactNumber(attention.length)} detail="Failed or blocked links" tone={attention.length ? 'bad' : 'good'} />
        <MetricCard label="Never checked" value={compactNumber(stale.length)} detail="Real links without health timestamp" tone={stale.length ? 'warn' : 'neutral'} />
      </div>

      {error ? <div className="v2-error">{error}</div> : null}
      {runtimeError ? <div className="v2-error">{runtimeError}</div> : null}
      {actionStatus ? <div className="v2-info">{actionStatus}</div> : null}

      <Section
        eyebrow="Provider health"
        title="Live connection diagnostics"
        description="Run health checks against connected platform credentials and review the last known provider status per model."
        actions={(
          <>
            <button type="button" className="v2-btn v2-btn-ghost" disabled={loading || !!busyKey} onClick={loadModels}>
              Refresh
            </button>
            <button type="button" className="v2-btn v2-btn-primary" disabled={!realRows.length || !!busyKey} onClick={runAllHealthChecks}>
              {busyKey ? 'Checking...' : 'Check all'}
            </button>
          </>
        )}
      >
        {rows.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>External account</th>
                  <th>Status</th>
                  <th>Last check</th>
                  <th>Error</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ model, link }) => {
                  const key = `${model.id}:${link.id || link.provider}`
                  const status = providerStatus(link)
                  const busy = busyKey === `${model.id}:${link.id}`
                  return (
                    <tr key={key}>
                      <td>{providerLabel(link.provider)}</td>
                      <td><Link to={`/v2/models/${model.id}`} className="v2-text-link">{modelTitle(model)}</Link></td>
                      <td>{link.external_id || link.email || link.username || 'Not provided'}</td>
                      <td><StatusPill tone={statusTone(status)}>{status}</StatusPill></td>
                      <td>{formatDate(link.connection_updated_at)}</td>
                      <td>{link.connection_error || 'None'}</td>
                      <td>
                        <div className="v2-action-row">
                          {link.id ? (
                            <button
                              type="button"
                              className="v2-button v2-button-secondary"
                              disabled={busy || !!busyKey}
                              onClick={() => runHealthCheck(model.id, link.id)}
                            >
                              {busy ? 'Checking...' : 'Check health'}
                            </button>
                          ) : (
                            <span>No account</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No provider slots found" description="Create a model or connect a provider account to start diagnostics." />
        )}
      </Section>

      <Section
        eyebrow="Provider smoke"
        title="Fanvue sync smoke"
        description="Run a small Fanvue catch-up pass without queuing imported messages for bot processing."
        actions={(
          <button type="button" className="v2-btn v2-btn-primary" disabled={!fanvueSyncRows.length || !!busyKey} onClick={runAllFanvueSyncSmokes}>
            {busyKey?.startsWith('fanvue-sync:') ? 'Syncing...' : 'Run all Fanvue sync'}
          </button>
        )}
      >
        {fanvueSyncRows.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>External account</th>
                  <th>Webhook</th>
                  <th>Webhook detail</th>
                  <th>Last check</th>
                  <th>Last result</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {fanvueSyncRows.map(({ model, link }) => {
                  const key = `fanvue-sync:${model.id}:${link.id}`
                  const busy = busyKey === key
                  const result = smokeResults[key]
                  return (
                    <tr key={key}>
                      <td><Link to={`/v2/models/${model.id}`} className="v2-text-link">{modelTitle(model)}</Link></td>
                      <td>{link.external_id || 'Not provided'}</td>
                      <td><StatusPill tone={statusTone(link.webhook_status || 'unknown')}>{link.webhook_status || 'unknown'}</StatusPill></td>
                      <td>{link.webhook_error || link.webhook_url || 'None'}</td>
                      <td>{formatDate(link.connection_updated_at || link.webhook_updated_at)}</td>
                      <td>
                        {result ? (
                          <div className="v2-action-row">
                            <StatusPill tone={result.tone}>{result.label}</StatusPill>
                            <span>{result.detail}</span>
                          </div>
                        ) : 'Not run'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="v2-button v2-button-secondary"
                          disabled={busy || !!busyKey}
                          onClick={() => runFanvueSyncSmoke(model, link)}
                        >
                          {busy ? 'Syncing...' : 'Sync smoke'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No connected Fanvue accounts" description="Connected Fanvue accounts will appear here after OAuth is complete." />
        )}
      </Section>

      <Section
        eyebrow="Provider smoke"
        title="Fansly sync smoke"
        description="Run a small Fansly catch-up pass without queuing imported messages for bot processing."
        actions={(
          <button type="button" className="v2-btn v2-btn-primary" disabled={!fanslySyncRows.length || !!busyKey} onClick={runAllFanslySyncSmokes}>
            {busyKey?.startsWith('fansly-sync:') ? 'Syncing...' : 'Run all Fansly sync'}
          </button>
        )}
      >
        {fanslySyncRows.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>External account</th>
                  <th>Webhook</th>
                  <th>Webhook detail</th>
                  <th>Last check</th>
                  <th>Last result</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {fanslySyncRows.map(({ model, link }) => {
                  const key = `fansly-sync:${model.id}:${link.id}`
                  const busy = busyKey === key
                  const result = smokeResults[key]
                  return (
                    <tr key={key}>
                      <td><Link to={`/v2/models/${model.id}`} className="v2-text-link">{modelTitle(model)}</Link></td>
                      <td>{link.external_id || 'Not provided'}</td>
                      <td><StatusPill tone={statusTone(link.webhook_status || 'unknown')}>{link.webhook_status || 'unknown'}</StatusPill></td>
                      <td>{link.webhook_error || link.webhook_url || 'None'}</td>
                      <td>{formatDate(link.connection_updated_at || link.webhook_updated_at)}</td>
                      <td>
                        {result ? (
                          <div className="v2-action-row">
                            <StatusPill tone={result.tone}>{result.label}</StatusPill>
                            <span>{result.detail}</span>
                          </div>
                        ) : 'Not run'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="v2-button v2-button-secondary"
                          disabled={busy || !!busyKey}
                          onClick={() => runFanslySyncSmoke(model, link)}
                        >
                          {busy ? 'Syncing...' : 'Sync smoke'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No connected Fansly accounts" description="Connected Fansly accounts will appear here after the account link is saved." />
        )}
      </Section>

      <Section
        eyebrow="Provider smoke"
        title="Telegram send smoke"
        description="Send a short real Telegram Account message to a fan with an explicit V2 Inbox peer mapping."
      >
        {telegramSmokeRows.length ? (
          <div className="v2-table-card">
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Account</th>
                  <th>Fan id</th>
                  <th>Message</th>
                  <th>Media ids</th>
                  <th>Last result</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {telegramSmokeRows.map(({ model, link }) => {
                  const key = `telegram-smoke:${model.id}:${link.id}`
                  const busy = busyKey === key
                  const draft = telegramSmokeDrafts[model.id] || {}
                  const result = smokeResults[key]
                  return (
                    <tr key={key}>
                      <td><Link to={`/v2/models/${model.id}`} className="v2-text-link">{modelTitle(model)}</Link></td>
                      <td>{link.external_id || 'Connected'}</td>
                      <td>
                        <input
                          value={draft.fanId || ''}
                          onChange={(event) => updateTelegramSmokeDraft(model.id, { fanId: event.target.value })}
                          placeholder="905"
                        />
                      </td>
                      <td>
                        <input
                          value={draft.text || 'Telegram account smoke test'}
                          onChange={(event) => updateTelegramSmokeDraft(model.id, { text: event.target.value })}
                          placeholder="Telegram account smoke test"
                        />
                      </td>
                      <td>
                        <input
                          value={draft.mediaIds || ''}
                          onChange={(event) => updateTelegramSmokeDraft(model.id, { mediaIds: event.target.value })}
                          placeholder="501, 502"
                        />
                      </td>
                      <td>
                        {result ? (
                          <div className="v2-action-row">
                            <StatusPill tone={result.tone}>{result.label}</StatusPill>
                            <span>{result.detail}</span>
                          </div>
                        ) : 'Not run'}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="v2-button v2-button-secondary"
                          disabled={busy || !!busyKey || !String(draft.fanId || '').trim()}
                          onClick={() => runTelegramSmoke(model, link)}
                        >
                          {busy ? 'Sending...' : 'Send smoke'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No connected Telegram accounts" description="Connected Telegram Account links will appear here after login is complete." />
        )}
      </Section>

      <Section
        eyebrow="Runtime traces"
        title="Bot and proactive traces"
        description="Review recent graph runs, proactive scheduler runs and analytics snapshots from the production backend."
        actions={(
          <button type="button" className="v2-btn v2-btn-ghost" disabled={runtimeLoading} onClick={loadRuntimeDiagnostics}>
            {runtimeLoading ? 'Refreshing...' : 'Refresh traces'}
          </button>
        )}
      >
        <div className="v2-diagnostic-grid">
          <DiagnosticCard title="Bot decision logs" value={compactNumber(botLogs.length)} detail="Recent graph instances" tone={botLogs.length ? 'good' : 'neutral'} />
          <DiagnosticCard title="Proactive runs" value={compactNumber(proactiveRuns.length)} detail="Recent scheduler runs" tone={proactiveRuns.length ? 'good' : 'neutral'} />
          <DiagnosticCard title="Analytics snapshots" value={compactNumber(analyticsSnapshots.length)} detail="Recent computed snapshots" tone={analyticsSnapshots.length ? 'good' : 'neutral'} />
          <DiagnosticCard
            title="Prompt regression"
            value={compactNumber(aiTestRuns.length)}
            detail={`${failedAiRuns.length} failed, ${runningAiRuns.length} running in recent AI test runs`}
            tone={failedAiRuns.length ? 'bad' : runningAiRuns.length ? 'warn' : aiTestRuns.length ? 'good' : 'neutral'}
          />
        </div>
        <RuntimeTraceTables
          botLogs={botLogs}
          proactiveRuns={proactiveRuns}
          analyticsSnapshots={analyticsSnapshots}
          aiTestRuns={aiTestRuns}
          runtimeLoading={runtimeLoading}
        />
      </Section>
    </div>
  )
}

function DiagnosticCard({ title, value = 'Ready', detail, tone }) {
  return (
    <article className="v2-diagnostic-card">
      <StatusPill tone={tone}>{value}</StatusPill>
      <h3>{title}</h3>
      <p>{detail}</p>
    </article>
  )
}

function RuntimeTraceTables({ botLogs, proactiveRuns, analyticsSnapshots, aiTestRuns, runtimeLoading }) {
  if (runtimeLoading) return <LoadingBlock label="Loading runtime traces..." />
  return (
    <div className="v2-stack">
      <TraceTable
        title="Recent bot logs"
        empty="No recent graph logs."
        columns={['Created', 'Model', 'Fan', 'Status', 'Trigger']}
        rows={botLogs.map((item) => [
          formatDate(item.created_at),
          item.model_email || item.model_id || 'Unknown',
          item.fan_username || item.fan_name || item.fan_id || 'Unknown',
          item.status || 'unknown',
          item.trigger || item.initiated_by || 'fan',
        ])}
      />
      <TraceTable
        title="Recent proactive runs"
        empty="No proactive runs found."
        columns={['Started', 'Type', 'Agent runs']}
        rows={proactiveRuns.map((item) => [
          formatDate(item.started_at),
          item.run_type || 'scheduled',
          item.agent_runs_count ?? 0,
        ])}
      />
      <TraceTable
        title="Recent analytics snapshots"
        empty="No analytics snapshots found."
        columns={['Computed', 'Scope', 'Type', 'Period']}
        rows={analyticsSnapshots.map((item) => [
          formatDate(item.computed_at),
          `${item.scope_type || 'scope'}:${item.scope_id || 'all'}`,
          item.snapshot_type || 'snapshot',
          item.period || 'current',
        ])}
      />
      <TraceTable
        title="Recent prompt regression runs"
        empty="No recent AI test runs found."
        columns={['Started', 'Scenario', 'Status', 'Passed / failed', 'Error']}
        rows={aiTestRuns.map((item) => [
          formatDate(item.started_at),
          item.scenario_name || item.scenario_slug || item.scenario_id || `Run #${item.id}`,
          <StatusPill tone={statusTone(item.status)}>{item.status || 'unknown'}</StatusPill>,
          `${item.passed_checks ?? 0} / ${item.failed_checks ?? 0}`,
          item.error || 'None',
        ])}
      />
    </div>
  )
}

function TraceTable({ title, empty, columns, rows }) {
  return (
    <div className="v2-table-card">
      <div className="v2-card-heading">
        <h3>{title}</h3>
      </div>
      {rows.length ? (
        <table className="v2-table">
          <thead>
            <tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${title}-${rowIndex}`}>
                {row.map((cell, cellIndex) => <td key={`${title}-${rowIndex}-${cellIndex}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState title={empty} description="Runtime history will appear here after the backend records activity." />
      )}
    </div>
  )
}
